import React, { useMemo, useState } from 'react';

type Teat = 'AE' | 'AD' | 'PE' | 'PD';

type CowTeatPickerProps = {
  backgroundSrc: string;
  value?: Teat[];
  defaultValue?: Teat[];
  onChange?: (next: Teat[]) => void;
  readOnly?: boolean;
  className?: string;
  labels?: Partial<Record<Teat, string>>;
  debug?: boolean;
  compact?: boolean; // reduz tamanho dos hotspots (úbere menor no resumo)
  teatPercent?: number; // override do percentual relativo para o tamanho do teto
  fontSizeOverride?: number; // override do tamanho da fonte nos tetos
};

const DEFAULT_LABELS: Record<Teat, string> = {
  AE: 'Teto anterior esquerdo',
  AD: 'Teto anterior direito',
  PE: 'Teto posterior esquerdo',
  PD: 'Teto posterior direito',
};

const POS: Record<Teat, { left: string; top: string }> = {
  // Ajuste fino: mais separação horizontal para evitar sobreposição em resumo
  AE: { left: '41%', top: '47%' },
  AD: { left: '59%', top: '47%' },
  PE: { left: '41%', top: '67%' },
  PD: { left: '59%', top: '67%' },
};

const POS_COMPACT: Record<Teat, { left: string; top: string }> = {
  AE: { left: '39%', top: '45%' },
  AD: { left: '61%', top: '45%' },
  PE: { left: '39%', top: '69%' },
  PD: { left: '61%', top: '69%' },
};

const CowTeatPicker: React.FC<CowTeatPickerProps> = ({
  backgroundSrc,
  value,
  defaultValue,
  onChange,
  readOnly,
  className,
  labels,
  debug,
  compact,
  teatPercent,
  fontSizeOverride,
}) => {
  const controlled = Array.isArray(value);
  const [internal, setInternal] = useState<Teat[]>(defaultValue || []);
  const selected = controlled ? (value as Teat[]) : internal;
  const mergedLabels = useMemo(() => ({ ...DEFAULT_LABELS, ...(labels || {}) }), [labels]);

  const dispatchChange = (next: Teat[]) => {
    try {
      document.dispatchEvent(new CustomEvent('teats:change', { detail: next }));
    } catch {}
    onChange?.(next);
  };

  const toggle = (t: Teat) => {
    if (readOnly) return;
    const isSelected = selected.includes(t);
    const next = isSelected ? selected.filter((x) => x !== t) : [...selected, t];
    if (!controlled) setInternal(next);
    dispatchChange(next);
  };

  // Permite variáveis CSS sem precisar suprimir erros de tipo
  const minSize = compact ? '38px' : '56px';
  const maxSize = compact ? '104px' : '128px';
  const percentSize = typeof teatPercent === 'number' ? `${teatPercent}%` : (compact ? '12%' : '14%');

  const containerStyle: React.CSSProperties & Record<string, string | number> = {
    position: 'relative',
    maxWidth: 480,
    width: '100%',
    aspectRatio: '1 / 1',
    backgroundImage: backgroundSrc ? `url(${backgroundSrc})` : 'none',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    // CSS variables
    '--teat': '#cfcfcf',
    '--teat-selected': '#e04b69',
    '--line': '#1f1f1f',
    '--focus': '#005fcc',
    '--teat-min': minSize,
    '--teat-max': maxSize,
    '--teat-percent': percentSize,
  };

  // Em modo compacto, reduzimos o mínimo para caber com leve espaçamento no resumo
  const btnWidth = 'clamp(var(--teat-min), var(--teat-percent), var(--teat-max))';
  const btnHeight = 'clamp(var(--teat-min), var(--teat-percent), var(--teat-max))';
  const fontSize = typeof fontSizeOverride === 'number' ? fontSizeOverride : (compact ? 11 : 12); // permitir override do texto
  const baseBtn: React.CSSProperties = {
    position: 'absolute',
    transform: 'translate(-50%, -50%)',
    width: btnWidth,
    height: btnHeight,
    borderRadius: 10,
    // Mantém borda apenas nas extremidades externas; será ajustado por botão
    border: '1.5px solid rgba(31,31,31,0.6)',
    background: 'var(--teat)',
    color: '#222',
    fontWeight: 600,
    fontSize,
    cursor: readOnly ? 'default' : 'pointer',
    outline: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'filter .15s ease',
  };

  return (
    <div className={className} style={containerStyle} aria-label="Selecionar tetos da vaca">
      <style>{`
        .teat-btn:hover { filter: brightness(1.03); }
        .teat-btn:focus-visible { outline: 3px solid var(--focus); outline-offset: 2px; }
      `}</style>

      {(Object.keys(POS) as Teat[]).map((code) => {
        const checked = selected.includes(code);
        const isLeft = code === 'AE' || code === 'PE';
        const isRight = code === 'AD' || code === 'PD';
        const outerRadius = compact ? 18 : 24;
        // Inner corners devem ser retas (sem arredondamento)
        const innerRadius = 0;
        const positions = compact ? POS_COMPACT : POS;
        const style: React.CSSProperties = {
          ...baseBtn,
          left: positions[code].left,
          top: positions[code].top,
          background: checked ? 'var(--teat-selected)' : 'var(--teat)',
          color: checked ? '#fff' : '#222',
        };

        // Remover bordas internas e arredondar pontas externas
        if (isLeft) {
          // Borda interna sutil (0.5px) e cantos internos retos
          style.borderRight = '0.5px solid rgba(31,31,31,0.35)';
          style.borderTopLeftRadius = outerRadius;
          style.borderBottomLeftRadius = outerRadius;
          style.borderTopRightRadius = innerRadius;
          style.borderBottomRightRadius = innerRadius;
        }
        if (isRight) {
          // Borda interna sutil (0.5px) e cantos internos retos
          style.borderLeft = '0.5px solid rgba(31,31,31,0.35)';
          style.borderTopRightRadius = outerRadius;
          style.borderBottomRightRadius = outerRadius;
          style.borderTopLeftRadius = innerRadius;
          style.borderBottomLeftRadius = innerRadius;
        }
        const label = mergedLabels[code] || code;
        return (
          <button
            key={code}
            type="button"
            className="teat-btn"
            style={style}
            role="switch"
            aria-checked={checked}
            aria-label={label}
            tabIndex={readOnly ? -1 : 0}
            onClick={() => toggle(code)}
            onKeyDown={(e) => {
              if (readOnly) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggle(code);
              }
            }}
          >
            {code}
          </button>
        );
      })}

      {debug && (
        <pre style={{ marginTop: 8, fontSize: 12, background: '#f7f7f7', border: '1px solid #e0e0e0', borderRadius: 6, padding: 8 }}>
          {JSON.stringify(selected)}
        </pre>
      )}
    </div>
  );
};

export default CowTeatPicker;
